import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

export const MAX_COMMENT_LENGTH = 200;

/** The student a frame was assigned to; null means a shared group frame. */
export type FrameOwner = number | null;

export type SealMark = {
  at: string;
  /** The student number that sealed it, or null when a teacher did. */
  by: number | null;
};

export type FrameComment = {
  id: string;
  at: string;
  authorLabel: string;
  authorRole: "TEACHER" | "STUDENT";
  /** The writing student's number — how a student recognises their own
   * comment to edit or delete it. Absent on teacher comments and on
   * comments written before it existed. */
  authorNumber?: number | null;
  text: string;
  /** Set once the comment's text has been edited. */
  editedAt?: string | null;
};

export type FrameColorSpec = {
  border: string;
  bg: string;
};

export const FRAME_PALETTE: readonly FrameColorSpec[] = [
  { border: "#2563eb", bg: "rgba(37, 99, 235, 0.05)" }, // 1: Blue
  { border: "#059669", bg: "rgba(5, 150, 105, 0.05)" }, // 2: Emerald
  { border: "#d97706", bg: "rgba(217, 119, 6, 0.05)" }, // 3: Amber
  { border: "#7c3aed", bg: "rgba(124, 58, 237, 0.05)" }, // 4: Violet
  { border: "#db2777", bg: "rgba(219, 39, 119, 0.05)" }, // 5: Pink
  { border: "#0891b2", bg: "rgba(8, 145, 178, 0.05)" }, // 6: Cyan
  { border: "#ea580c", bg: "rgba(234, 88, 12, 0.05)" }, // 7: Orange
  { border: "#4f46e5", bg: "rgba(79, 70, 229, 0.05)" }, // 8: Indigo
  { border: "#16a34a", bg: "rgba(22, 163, 74, 0.05)" }, // 9: Green
  { border: "#c026d3", bg: "rgba(192, 38, 211, 0.05)" }, // 10: Fuchsia
  { border: "#0284c7", bg: "rgba(2, 132, 199, 0.05)" }, // 11: Sky Blue
  { border: "#ca8a04", bg: "rgba(202, 138, 4, 0.05)" }, // 12: Gold
] as const;

export type FrameMeta = {
  owner: FrameOwner;
  sealed: SealMark | null;
  comments: FrameComment[];
  worksheetLocked?: boolean;
  /** When the teacher last switched to focus mode; students jump to their
   * own frame each time it changes. */
  worksheetLockedAt?: string | null;
  /** Help mode (도움모드): on an individual canvas, students may edit each
   * other's frames. Absent means focus mode (집중모드), the default. */
  helpMode?: boolean;
  frameColor?: string | null;
  frameBgColor?: string | null;
  /** The background the frame's owner picked. Kept apart from
   * `frameBgColor`, which focus mode overwrites on every toggle. */
  background?: string | null;
};

export const EMPTY_META: FrameMeta = {
  owner: null,
  sealed: null,
  comments: [],
  worksheetLocked: false,
  worksheetLockedAt: null,
  helpMode: false,
  frameColor: null,
  frameBgColor: null,
  background: null,
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readOwner = (value: unknown): FrameOwner =>
  typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null;

const readSeal = (value: unknown): SealMark | null => {
  const record = asRecord(value);
  if (!record || typeof record.at !== "string") {
    return null;
  }
  return {
    at: record.at,
    by: typeof record.by === "number" ? record.by : null,
  };
};

const readComment = (value: unknown): FrameComment | null => {
  const record = asRecord(value);
  if (
    !record ||
    typeof record.id !== "string" ||
    typeof record.at !== "string" ||
    typeof record.text !== "string" ||
    typeof record.authorLabel !== "string"
  ) {
    return null;
  }
  return {
    id: record.id,
    at: record.at,
    authorLabel: record.authorLabel,
    authorRole: record.authorRole === "STUDENT" ? "STUDENT" : "TEACHER",
    authorNumber: readOwner(record.authorNumber),
    text: record.text.slice(0, MAX_COMMENT_LENGTH),
    editedAt: typeof record.editedAt === "string" ? record.editedAt : null,
  };
};

/**
 * Anything on the wire may be malformed — another client's build, a restored
 * revision — so a bad field degrades to its empty value instead of throwing.
 */
export const readFrameMeta = (element: ExcalidrawElement): FrameMeta => {
  const namespace = asRecord(asRecord(element.customData)?.canvaschool);
  if (!namespace) {
    return { ...EMPTY_META, comments: [] };
  }
  const rawComments = Array.isArray(namespace.comments)
    ? namespace.comments
    : [];
  return {
    owner: readOwner(namespace.owner),
    sealed: readSeal(namespace.sealed),
    comments: rawComments
      .map(readComment)
      .filter((comment): comment is FrameComment => comment !== null),
    worksheetLocked: Boolean(namespace.worksheetLocked),
    worksheetLockedAt:
      typeof namespace.worksheetLockedAt === "string"
        ? namespace.worksheetLockedAt
        : null,
    helpMode: namespace.helpMode === true,
    frameColor:
      typeof namespace.frameColor === "string" ? namespace.frameColor : null,
    frameBgColor:
      typeof namespace.frameBgColor === "string"
        ? namespace.frameBgColor
        : null,
    background:
      typeof namespace.background === "string" ? namespace.background : null,
  };
};

export const writeFrameMeta = <Element extends ExcalidrawElement>(
  element: Element,
  meta: FrameMeta,
): Element => ({
  ...element,
  customData: {
    ...(asRecord(element.customData) ?? {}),
    canvaschool: {
      owner: meta.owner,
      sealed: meta.sealed,
      comments: meta.comments,
      worksheetLocked: meta.worksheetLocked ?? false,
      worksheetLockedAt: meta.worksheetLockedAt ?? null,
      helpMode: meta.helpMode ?? false,
      frameColor: meta.frameColor ?? null,
      frameBgColor: meta.frameBgColor ?? null,
      background: meta.background ?? null,
    },
  },
});

const mapFrame = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  change: (meta: FrameMeta) => FrameMeta,
): ExcalidrawElement[] =>
  elements.map((element) =>
    element.id === frameId
      ? writeFrameMeta(element, change(readFrameMeta(element)))
      : element,
  );

/** Sealing writes only the frame's own metadata — never its contents. */
export const sealFrame = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  by: number | null,
  at: string = new Date().toISOString(),
): ExcalidrawElement[] =>
  mapFrame(elements, frameId, (meta) => ({ ...meta, sealed: { at, by } }));

export const unsealFrame = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
): ExcalidrawElement[] =>
  mapFrame(elements, frameId, (meta) => ({ ...meta, sealed: null }));

/** Null clears the background. */
export const setFrameBackground = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  background: string | null,
): ExcalidrawElement[] =>
  mapFrame(elements, frameId, (meta) => ({ ...meta, background }));

/** Comments live in the frame's own metadata — the one frame change the
 * shared student rule (`studentChangePolicy`) lets a student make. */
export const addComment = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  comment: FrameComment,
): ExcalidrawElement[] =>
  mapFrame(elements, frameId, (meta) => ({
    ...meta,
    comments: [...meta.comments, comment],
  }));

export const removeComment = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  commentId: string,
): ExcalidrawElement[] =>
  mapFrame(elements, frameId, (meta) => ({
    ...meta,
    comments: meta.comments.filter((comment) => comment.id !== commentId),
  }));

export const editComment = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  commentId: string,
  text: string,
  at: string = new Date().toISOString(),
): ExcalidrawElement[] =>
  mapFrame(elements, frameId, (meta) => ({
    ...meta,
    comments: meta.comments.map((comment) =>
      comment.id === commentId
        ? { ...comment, text: text.slice(0, MAX_COMMENT_LENGTH), editedAt: at }
        : comment,
    ),
  }));

/**
 * Whether this viewer may edit or delete a comment: a teacher any comment,
 * a student only their own. Older comments carry no `authorNumber`, so a
 * student's own is recognised by its label there.
 */
export const canManageComment = (
  comment: FrameComment,
  viewer: { isTeacher: boolean; number: number | null; label: string },
): boolean => {
  if (viewer.isTeacher) {
    return true;
  }
  if (comment.authorRole !== "STUDENT" || viewer.number === null) {
    return false;
  }
  return comment.authorNumber != null
    ? comment.authorNumber === viewer.number
    : comment.authorLabel === viewer.label;
};

/**
 * Marks (or unmarks) teacher material. The teacher's editor tags everything
 * it creates; the shared student rule reads the same `canvaschool.worksheet`
 * flag through `isWorksheetElement`. Unlike the core's `locked`, it leaves
 * the element selectable for the teacher.
 */
export const withWorksheetTag = <Element extends ExcalidrawElement>(
  element: Element,
  tagged: boolean,
): Element => withCanvaschoolFlag(element, "worksheet", tagged);

/**
 * Marks (or unmarks) an element the teacher deleted. The shared student rule
 * reads it through `isDeletedByTeacher`, so no student can bring it back.
 */
export const withTeacherDeletionMark = <Element extends ExcalidrawElement>(
  element: Element,
  marked: boolean,
): Element => withCanvaschoolFlag(element, "deletedByTeacher", marked);

const withCanvaschoolFlag = <Element extends ExcalidrawElement>(
  element: Element,
  flag: "worksheet" | "deletedByTeacher",
  on: boolean,
): Element => {
  const customData = { ...(asRecord(element.customData) ?? {}) };
  const canvaschool = { ...(asRecord(customData.canvaschool) ?? {}) };
  if (on) {
    canvaschool[flag] = true;
  } else {
    delete canvaschool[flag];
  }
  if (Object.keys(canvaschool).length > 0) {
    customData.canvaschool = canvaschool;
  } else {
    delete customData.canvaschool;
  }
  return { ...element, customData };
};
